import { inject, Injectable } from '@angular/core';
import { RemoteConfigModel } from '@libs/shared/models';
import { first, map, Observable } from 'rxjs';
import { AppStateService } from '../aktia-app-state';
import {
  AppEnvironmentModel,
  CmsEnvironmentModel,
  ConsentConfigModel,
  ENVIRONMENT,
  EnvironmentModel,
  EnvironmentServiceModel,
  EnvironmentType,
  GrafanaFaroConfigModel,
  LinkConfigModel,
  MsalConfigModel,
} from './environment.model';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService implements EnvironmentServiceModel {
  private readonly env: EnvironmentModel = inject(ENVIRONMENT);
  private readonly appStateService: AppStateService = inject(AppStateService);

  public links(): LinkConfigModel {
    return this.env.links;
  }

  public type(): EnvironmentType {
    return this.env.type;
  }

  public production(): boolean {
    return this.env.production;
  }

  public app(): Readonly<AppEnvironmentModel> {
    return this.env.app ?? this.throwError('Application');
  }

  public appVersion(): Readonly<string> {
    return this.env.appVersion;
  }

  public cms(): CmsEnvironmentModel {
    return this.env.cms ?? this.throwError('CMS');
  }

  public bff(): Observable<RemoteConfigModel> {
    const config: RemoteConfigModel = this.env.bff ?? this.throwError('Backend');

    return this.appStateService.get('bffUrl').pipe(
      first(),
      map(url => ({
        ...config,
        url: url ?? config.url,
      })),
    );
  }

  public liveUpdateUrl(): Readonly<string> {
    return this.env.liveUpdateUrl;
  }

  public msalConfig(): Readonly<MsalConfigModel> {
    return this.env.msalConfig;
  }

  public msalLekkerlandDirectoryUserConfig(): Readonly<MsalConfigModel> {
    return this.env.msalLekkerlandDirectoryUserConfig;
  }

  public consentConfig(): Readonly<ConsentConfigModel | undefined> {
    return this.env.consentConfig;
  }

  public grafanaFaroConfig(): Readonly<GrafanaFaroConfigModel> {
    return this.env.grafanaFaroConfig;
  }

  private throwError(name: string): never {
    throw new Error(`${name} environment is not defined`);
  }
}
